import React from "react";

import { InputNumber } from "antd";

import { BackingSellerSelect } from "./selects";
import { FLAGS } from "./helpers";

const MUTED = "rgba(0,0,0,0.45)";
const AMBER = "#ad4e00";

const editableStyle = { cursor: "pointer", borderBottom: "1px dashed #bfbfbf" };

/**
 * Click to edit a number in place (`min` defaults to 0; `precision` passes to InputNumber): Enter or clicking away saves (an unchanged draft just closes), Esc cancels.
 * `onSave({ value })` returns a Promise; the cell stays open if it rejects.
 */
export class EditableNumberCell extends React.Component {

    state = { editing: false, draft: undefined, saving: false };

    start = () => this.setState({ editing: true, draft: this.props.value === null ? undefined : this.props.value });

    cancel = () => !this.state.saving && this.setState({ editing: false });

    save = async () => {
        if (this.state.saving) return;
        const unchanged = (this.state.draft === undefined ? null : this.state.draft) === (this.props.value === undefined ? null : this.props.value);
        if (unchanged) {
            this.setState({ editing: false });
            return;
        }
        this.setState({ saving: true });
        try {
            await this.props.onSave({ value: this.state.draft });
            this.setState({ editing: false, saving: false });
        } catch (e) {
            this.setState({ saving: false });
        }
    };

    onKeyDown = e => {
        if (e.key === "Enter") this.save();
        if (e.key === "Escape") this.cancel();
    };

    render() {
        const { readOnly, label, min, precision, children } = this.props;
        const { editing, draft, saving } = this.state;
        if (readOnly) return children;
        if (!editing) {
            return <span role="button" tabIndex={0} title={`Edit ${label}`} style={editableStyle} onClick={this.start} onKeyDown={e => e.key === "Enter" && this.start()}>{children}</span>;
        }
        return (
            <InputNumber
                autoFocus
                min={min === undefined ? 0 : min}
                precision={precision}
                aria-label={label}
                disabled={saving}
                value={draft}
                style={{ width: 110 }}
                onChange={value => this.setState({ draft: value })}
                onKeyDown={this.onKeyDown}
                onBlur={this.save}
            />
        );
    }
}

/** Click to pick another backing supplier; choosing saves, clearing removes it, Esc cancels. */
export class EditableBackingCell extends React.Component {

    state = { editing: false };

    save = async ({
        value,
    }) => {
        try {
            await this.props.onSave({ value });
            this.setState({ editing: false });
        } catch (e) {
            // stays open for another pick, like EditableNumberCell; the error toast comes from onSave
        }
    };

    render() {
        const { line, readOnly } = this.props;
        const label = line.backingSellerName || <span style={{ color: AMBER }}>Not set</span>;
        if (readOnly) return label;
        if (!this.state.editing) {
            return (
                <span role="button" tabIndex={0} style={editableStyle}
                    onClick={() => this.setState({ editing: true })}
                    onKeyDown={e => e.key === "Enter" && this.setState({ editing: true })}>
                    {label}
                </span>
            );
        }
        return (
            <BackingSellerSelect
                autoFocus
                defaultOpen
                style={{ width: 220 }}
                value={line.backingSellerId}
                initialOption={line.backingSellerId ? { value: line.backingSellerId, label: line.backingSellerName } : null}
                onChange={value => this.save({ value })}
                onInputKeyDown={e => e.key === "Escape" && this.setState({ editing: false })}
                onBlur={() => this.setState({ editing: false })}
            />
        );
    }
}

/** Image, title, SKU · ID, and the line's live flags as text, except "no backing supplier" (its column already says "Not set"). */
export const ProductCell = ({ line }) => {
    const product = line.product;
    const flagTexts = FLAGS.filter(f => f.name !== "noBackingSeller" && line.flags && line.flags[f.name]).map(f => f.text);
    return (
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {product && product.image
                ? <img src={product.image} alt="" style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 4, flexShrink: 0 }} />
                : <div style={{ width: 40, height: 40, borderRadius: 4, background: "#f5f5f5", flexShrink: 0 }} />}
            <div style={{ minWidth: 0 }}>
                <div>{product ? product.title : <i>Product not found</i>}</div>
                <div style={{ color: MUTED, fontSize: 12 }}>
                    {product && product.sku ? `SKU ${product.sku} · ` : ""}ID {`${line.productId}`}
                </div>
                {flagTexts.map(text => <div key={text} style={{ color: AMBER, fontSize: 12 }}>{text}</div>)}
            </div>
        </div>
    );
};



// WEBPACK FOOTER //
// ./src/components/term-contract/line-cells.js